import { useTranslation } from "react-i18next";
import { Alert, CircularProgress, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import type { ContentStatement } from "@shared/content-statement";
import { tokens } from "../../theme";
import { tvodStaffApi } from "../../http/tvodStaff";
import { friendlyError } from "../../utils/apiError";
import { StatementView, dayOf, type StatementRange } from "./StatementView";

/**
 * Nenda staff's view of ONE provider's statement - the same statement, drawn
 * by the same `StatementView`, the provider sees on its own Revenue page.
 *
 * It is about the provider's TITLES, not its portal: a title of this
 * provider sold on another provider's portal is here, and a title of another
 * provider sold on this provider's portal is not (R15).
 */
export default function ProviderRevenuePanel({
  ownerId,
  ownerName,
}: {
  ownerId: string;
  ownerName: string;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const [statement, setStatement] = useState<ContentStatement | null>(null);
  const [range, setRange] = useState<StatementRange | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (asked?: StatementRange) => {
      try {
        const s = await tvodStaffApi.statement(ownerId, asked);
        setStatement(s);
        // Echo the window the SERVER used, not the one we asked for.
        setRange({ from: dayOf(s.from), to: dayOf(s.to) });
        setError(null);
      } catch (e) {
        const code = (e as { code?: string }).code;
        setError(
          code === "invalid_period"
            ? t("providerRevenuePanel.invalidPeriod")
            : friendlyError(t, e)
        );
      }
    },
    [ownerId, t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
        {t("providerRevenuePanel.intro", { name: ownerName })}
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!statement && !error ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={26} />
        </Stack>
      ) : null}
      {statement ? (
        <StatementView
          statement={statement}
          range={range}
          onRangeChange={setRange}
          onApply={(r) => void load(r)}
          shareLabel={t("providerRevenuePanel.providersShare")}
          csvName={`statement-${ownerId}`}
        />
      ) : null}
    </Stack>
  );
}
